import React, { createContext, useContext, useState, useEffect } from 'react'
import type { User, UserConsignee, Address } from '@/types'

interface CustomerAuthContextType {
  user: User | null
  isLoggedIn: boolean
  loginWithGoogle: () => void
  logout: () => void
  updateUserProfile: (profile: Partial<User>, address?: Partial<Address>) => void
  savedAddresses: UserConsignee[]
  addSavedAddress: (consignee: Partial<Address>, title?: string) => void
  removeSavedAddress: (userConsigneeId: number) => void
}

const defaultUserAddress: Address = {
  AddressId: 101,
  Name: '山田 太郎 (Googleアカウント)',
  Furigana: 'ヤマダ タロウ',
  PostalCD: '1500043',
  PrefectureName: '東京都',
  CityName: '渋谷区',
  Address1: '道玄坂1-2-3',
  Address2: '渋谷ビル 4F',
  Phone: '090-1234-5678',
  MailAddress: 'taro.yamada.google@example.com',
  CreatedAt: new Date().toISOString(),
  UpdatedAt: new Date().toISOString(),
  IsActive: true,
}

const initialUser: User = {
  UserId: 1,
  GoogleId: 'google-oauth2-10928374918237',
  Email: 'taro.yamada.google@example.com',
  Name: '山田 太郎',
  Furigana: 'ヤマダ タロウ',
  Phone: '090-1234-5678',
  AddressId: 101,
  ShipperId: 1,
  Address: defaultUserAddress,
  CreatedAt: new Date().toISOString(),
  UpdatedAt: new Date().toISOString(),
}

const initialSavedAddresses: UserConsignee[] = [
  {
    UserConsigneeId: 1,
    UserId: 1,
    ConsigneeId: 201,
    Title: '実家（大阪）',
    IsDefault: true,
    CreatedAt: new Date().toISOString(),
    Consignee: {
      ConsigneeId: 201,
      AddressId: 102,
      IsActive: true,
      CreatedAt: new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
      Address: {
        AddressId: 102,
        Name: '山田 花子',
        Furigana: 'ヤマダ ハナコ',
        PostalCD: '5300001',
        PrefectureName: '大阪府',
        CityName: '大阪市北区',
        Address1: '梅田2-4-9',
        Address2: 'タワーマンション802',
        Phone: '06-6123-4567',
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        IsActive: true,
      },
    },
  },
  {
    UserConsigneeId: 2,
    UserId: 1,
    ConsigneeId: 202,
    Title: '佐藤 健太 様宛',
    IsDefault: false,
    CreatedAt: new Date().toISOString(),
    Consignee: {
      ConsigneeId: 202,
      AddressId: 103,
      IsActive: true,
      CreatedAt: new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
      Address: {
        AddressId: 103,
        Name: '佐藤 健太',
        Furigana: 'サトウ ケンタ',
        PostalCD: '9800811',
        PrefectureName: '宮城県',
        CityName: '仙台市青葉区',
        Address1: '一番町4-1-1',
        Phone: '022-222-3333',
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        IsActive: true,
      },
    },
  },
]

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined)

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('highlandirect_customer_user')
    return stored ? JSON.parse(stored) : initialUser
  })

  const [savedAddresses, setSavedAddresses] = useState<UserConsignee[]>(() => {
    const stored = localStorage.getItem('highlandirect_customer_addresses')
    return stored ? JSON.parse(stored) : initialSavedAddresses
  })

  useEffect(() => {
    if (user) {
      localStorage.setItem('highlandirect_customer_user', JSON.stringify(user))
    } else {
      localStorage.removeItem('highlandirect_customer_user')
    }
  }, [user])

  useEffect(() => {
    localStorage.setItem('highlandirect_customer_addresses', JSON.stringify(savedAddresses))
  }, [savedAddresses])

  const loginWithGoogle = () => {
    setUser(initialUser)
  }

  const logout = () => {
    setUser(null)
  }

  const updateUserProfile = (profile: Partial<User>, addressData?: Partial<Address>) => {
    if (!user) return
    const updatedAddress = addressData && user.Address ? { ...user.Address, ...addressData } : user.Address
    const updatedUser: User = {
      ...user,
      ...profile,
      Address: updatedAddress,
      UpdatedAt: new Date().toISOString(),
    }
    setUser(updatedUser)
  }

  const addSavedAddress = (addressData: Partial<Address>, title?: string) => {
    if (!user) return
    const newAddressId = Date.now()
    const newConsigneeId = Date.now() + 1
    const newUserConsigneeId = Date.now() + 2

    const newAddress: Address = {
      AddressId: newAddressId,
      Name: addressData.Name || '',
      Furigana: addressData.Furigana || '',
      PostalCD: addressData.PostalCD || '',
      PrefectureName: addressData.PrefectureName || '',
      CityName: addressData.CityName || '',
      Address1: addressData.Address1 || '',
      Address2: addressData.Address2 || '',
      Phone: addressData.Phone || '',
      CreatedAt: new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
      IsActive: true,
    }

    const newEntry: UserConsignee = {
      UserConsigneeId: newUserConsigneeId,
      UserId: user.UserId,
      ConsigneeId: newConsigneeId,
      Title: title || `${addressData.Name}様宛`,
      IsDefault: savedAddresses.length === 0,
      CreatedAt: new Date().toISOString(),
      Consignee: {
        ConsigneeId: newConsigneeId,
        AddressId: newAddressId,
        IsActive: true,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
        Address: newAddress,
      },
    }

    setSavedAddresses((prev) => [newEntry, ...prev])
  }

  const removeSavedAddress = (userConsigneeId: number) => {
    setSavedAddresses((prev) => prev.filter((item) => item.UserConsigneeId !== userConsigneeId))
  }

  return (
    <CustomerAuthContext.Provider
      value={{
        user,
        isLoggedIn: !!user,
        loginWithGoogle,
        logout,
        updateUserProfile,
        savedAddresses,
        addSavedAddress,
        removeSavedAddress,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  )
}

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext)
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider')
  }
  return context
}
